// Types for User API based on swagger schema
import { CollectionDto } from "./collection";
import { ProductDto } from "./product";
import { LookDto } from "./look";
import { SubscriptionLevel } from "./subscription";

export enum Role {
  USER = "USER",
  STYLIST = "STYLIST",
}

export enum StylePreference {
  MENSWEAR = "MENSWEAR",
  WOMENSWEAR = "WOMENSWEAR",
  UNISEX = "UNISEX",
}

/**
 * User preferences DTO - lightweight preferences data
 */
export interface UserPreferencesDto {
  stylePreference: StylePreference;
  preferredCurrency: string;
}

/**
 * Basic user information
 */
export interface UserSummaryDto {
  id: string;
  username: string;
  name?: string | null;
  bio?: string | null;
  avatarUrl?: string | null;
  coverUrl?: string | null;
  website?: string | null;
  preferredCurrency?: string;
  role: Role;
}

/**
 * Account deletion request information
 */
export interface AccountDeletionRequest {
  id: string;
  requestedAt: string;
  scheduledFor: string;
  reason?: string;
  status: "PENDING" | "CANCELLED" | "PROCESSED";
}

/**
 * Extended user profile with collections and subscription info
 */
export interface ProfileDto {
  id: string;
  username: string;
  name?: string | null;
  bio?: string | null;
  website?: string | null;
  avatarUrl?: string | null;
  coverUrl?: string | null;
  preferredCurrency?: string;
  stylePreference?: StylePreference;
  role: Role;
  instagramFollowerCount?: number | null;
  subscribedToCount: number;
  subscriberCount: number;
  subscriptionLevel?: SubscriptionLevel | null;
  publicCollections: CollectionDto[];
  privateAndSavedCollections: CollectionDto[];
  deletionRequest?: AccountDeletionRequest;
  createdAt: string;
  updatedAt: string;
}

/**
 * Parameters for fetching users
 */
export interface GetUsersParams {
  page?: number;
  limit?: number;
}

/**
 * Request to update a user profile
 */
export interface UpdateUserRequest {
  name?: string;
  bio?: string;
  username?: string;
  website?: string;
  avatarUrl?: string;
  coverUrl?: string;
  preferredCurrency?: string;
}

/**
 * Response for paginated user lists
 */
export interface PaginatedUsersResponse {
  users: UserSummaryDto[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

/**
 * Response for user's subscribers (followers) list
 */
export interface SubscribersResponse {
  subscribers: UserSummaryDto[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

/**
 * Response for users that a user subscribes to (following)
 */
export interface SubscriptionsResponse {
  subscriptions: UserSummaryDto[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

/**
 * Response for user's products list
 */
export interface UserProductsResponse {
  products: ProductDto[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

/**
 * Response for user's looks list
 */
export interface UserLooksResponse {
  looks: LookDto[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

/**
 * Response for user's collections list
 */
export interface UserCollectionsResponse {
  collections: CollectionDto[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}
